/**
 * Cloudflare Turnstile (captcha da Cloudflare, gratis) do modal "Inscrever Beta" (dono, 2026-10-10).
 * Site key = VITE_TURNSTILE_SITE_KEY no build; vazia = sem captcha (o componente nao renderiza nada e a API, com
 * TURNSTILE_ENABLED=false, nao exige). O token vale UMA vez: depois de cada envio, reset().
 * Docs: https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/
 */
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

const SITE_KEY: string = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export const isTurnstileEnabled = () => Boolean(SITE_KEY);

type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
};
const api = () => (window as unknown as { turnstile?: TurnstileApi }).turnstile;

// Carrega o script uma vez so' (modo explicit: o widget so' aparece quando a gente chama render)
let loader: Promise<void> | null = null;
function loadTurnstile(): Promise<void> {
  if (api()) return Promise.resolve();
  if (loader) return loader;
  loader = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_URL;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => {
      loader = null;
      reject(new Error('turnstile'));
    };
    document.head.appendChild(s);
  });
  return loader;
}

export type TurnstileHandle = { reset: () => void };

type Props = { onToken: (token: string) => void };

export const Turnstile = forwardRef<TurnstileHandle, Props>(function Turnstile({ onToken }, ref) {
  const box = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  const cb = useRef(onToken);
  cb.current = onToken;

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        const ts = api();
        if (ts && widget.current) ts.reset(widget.current);
        cb.current('');
      },
    }),
    []
  );

  useEffect(() => {
    if (!SITE_KEY) return;
    let alive = true;
    loadTurnstile()
      .then(() => {
        const ts = api();
        if (!alive || !box.current || !ts) return;
        widget.current = ts.render(box.current, {
          sitekey: SITE_KEY,
          theme: 'dark',
          language: 'pt-br',
          size: 'flexible',
          callback: (token: string) => cb.current(token),
          'expired-callback': () => cb.current(''),
          'error-callback': () => cb.current(''),
        });
      })
      .catch(() => cb.current(''));
    return () => {
      alive = false;
      const ts = api();
      if (ts && widget.current) ts.remove(widget.current);
      widget.current = null;
    };
  }, []);

  if (!SITE_KEY) return null;
  return <div ref={box} className="nxb-turnstile" />;
});
